import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Undo as ReverseIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AssetStatusChip, fmtDate, getErrorMessage, unwrap } from './AssetsShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'CANCELLED', 'REVERSED'];

// Depreciation runs (verified against DepreciationController): create with
// any date inside the charge month (optionally restricted to chosen assets),
// preview the same month without persisting, then DRAFT -> APPROVED ->
// POSTED (consolidated DR expense / CR accumulated entry). DRAFT runs cancel,
// POSTED runs reverse with a reason.
const DepreciationTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.ASSETS, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.ASSETS, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 10));
  const [assetIds, setAssetIds] = useState('');
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reverseDlg, setReverseDlg] = useState({ open: false, item: null, reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);
  const idOf = (r) => r?.run_id ?? r?.id;
  const parseAssetIds = () =>
    String(assetIds || '')
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isInteger(n) && n > 0);

  const submitCreate = async () => {
    if (!month) {
      showSnackbar('Month is required', 'error');
      return;
    }
    try {
      setSaving(true);
      const ids = parseAssetIds();
      const res = await ledgerAPI.createDepreciationRun({
        month,
        ...(ids.length ? { asset_ids: ids } : {}),
      });
      const run = res?.data?.data ?? res?.data ?? {};
      showSnackbar(`Depreciation run ${run.run_number || (run.run_id ? `for ${month}` : '')} created as DRAFT`, 'success');
      setShowCreate(false);
      setAssetIds('');
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!month) {
      showSnackbar('Month is required', 'error');
      return;
    }
    try {
      setPreviewLoading(true);
      const ids = parseAssetIds();
      const res = await ledgerAPI.previewDepreciationRun({
        month,
        ...(ids.length ? { asset_ids: ids } : {}),
      });
      setPreview(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getDepreciationRun(idOf(row));
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch run'), 'error');
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this depreciation run?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Depreciation run ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitReverse = async () => {
    if ((reverseDlg.reason?.trim() ?? '').length < 5) {
      showSnackbar('Reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.reverseDepreciationRun(idOf(reverseDlg.item), { reason: reverseDlg.reason.trim() });
      showSnackbar('Run reversed successfully', 'success');
      setReverseDlg({ open: false, item: null, reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailSchedules = Array.isArray(detail?.schedules) ? detail.schedules : (Array.isArray(detail?.lines) ? detail.lines : []);
  const previewLines = Array.isArray(preview?.schedules) ? preview.schedules : (Array.isArray(preview?.lines) ? preview.lines : (Array.isArray(preview) ? preview : []));

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.ASSETS}
        title="Depreciation"
        subtitle="Monthly charges per category policy, posted as one consolidated entry"
        addLabel="New Depreciation Run"
        idKey="run_id"
        searchPlaceholder="Search runs..."
        emptyMessage="No depreciation runs found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getDepreciationRuns(params)}
        onAdd={canCreate ? () => { setMonth(new Date().toISOString().slice(0, 10)); setAssetIds(''); setPreview(null); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
        ]}
        columns={[
          { label: 'Run #', render: (r) => <strong>{r.run_number || `RUN-${idOf(r)}`}</strong> },
          { label: 'Month', render: (r) => fmtDate(r.month ?? r.run_date) },
          { label: 'Assets', render: (r) => r.asset_count ?? (Array.isArray(r.schedules) ? r.schedules.length : '-'), align: 'right' },
          { label: 'Charge', render: (r) => formatAccountingAmount(r.total_amount ?? r.total_depreciation), align: 'right' },
          { label: 'Status', render: (r) => <AssetStatusChip status={r.status} /> },
        ]}
        rowActions={(r) => {
          const id = idOf(r);
          const status = String(r.status || 'DRAFT').toUpperCase();
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(r) }];
          if (status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve run', 'approved', () => ledgerAPI.approveDepreciationRun(id)) });
            actions.push({ icon: CancelIcon, title: 'Cancel run', color: 'error', onClick: () => runWorkflow('Cancel run', 'cancelled', () => ledgerAPI.cancelDepreciationRun(id)) });
          }
          if (status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post run', 'posted', () => ledgerAPI.postDepreciationRun(id)) });
          }
          if (status === 'POSTED' && canApprove) {
            actions.push({ icon: ReverseIcon, title: 'Reverse', color: 'error', onClick: () => setReverseDlg({ open: true, item: r, reason: '' }) });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Depreciation Run</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth size="small" type="date" label="Any date in the charge month" value={month}
                onChange={(e) => setMonth(e.target.value)} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth size="small" label="Asset IDs (optional, comma-separated - empty = all due)"
                value={assetIds} onChange={(e) => setAssetIds(e.target.value)}
                placeholder="e.g. 12, 15, 21"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" startIcon={<PreviewIcon />} onClick={runPreview} disabled={previewLoading}>
              {previewLoading ? 'Previewing...' : 'Preview charges (nothing is saved)'}
            </Button>
          </Box>
          {preview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Preview</Typography>
              <DataTable
                columns={['Asset', 'Charge']}
                data={previewLines}
                loading={false}
                emptyMessage="Nothing due for this month."
                renderRow={(l, i) => (
                  <TableRow key={l.schedule_id ?? l.asset_id ?? i}>
                    <TableCell>{l.asset?.asset_name || (l.asset_id ? `Asset ${l.asset_id}` : '-')}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(l.depreciation_amount ?? l.amount)}</TableCell>
                  </TableRow>
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft Run'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Run {detail?.run_number || (detail ? `RUN-${idOf(detail)}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {fmtDate(detail.month ?? detail.run_date)}
              </Typography>
              <DataTable
                columns={['Asset', 'Charge', 'Schedule Status']}
                data={detailSchedules}
                loading={false}
                emptyMessage="No schedules."
                renderRow={(l, i) => (
                  <TableRow key={l.schedule_id ?? i}>
                    <TableCell>{l.asset?.asset_name || (l.asset_id ? `Asset ${l.asset_id}` : '-')}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(l.depreciation_amount ?? l.amount)}</TableCell>
                    <TableCell>{l.status || '-'}</TableCell>
                  </TableRow>
                )}
              />
              <Typography variant="body2" align="right" sx={{ mt: 1 }}>
                Total: <strong>{formatAccountingAmount(detail.total_amount ?? detail.total_depreciation)}</strong>
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={reverseDlg.open}
        onClose={() => setReverseDlg({ open: false, item: null, reason: '' })}
        title="Reverse depreciation run"
        onSubmit={submitReverse}
        values={reverseDlg}
        onChange={(key, value) => setReverseDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'reason', label: 'Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default DepreciationTab;
